import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { leaveService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';

interface LeaveApprovalModalProps {
  open: boolean;
  onClose: () => void;
  data?: {
    leaveId?: string;
    action?: 'approve' | 'reject';
  };
}

const LeaveApprovalModal: React.FC<LeaveApprovalModalProps> = ({ open, onClose, data }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbar();
  const [comments, setComments] = useState('');
  const action = data?.action === 'reject' ? 'reject' : 'approve';
  const leaveId = data?.leaveId;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingApprovals });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myLeaves });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calendarEvents });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leaveBalance });
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!leaveId) throw new Error('Leave request is missing');
      if (action === 'approve') {
        return leaveService.approveLeave(leaveId, comments.trim() || undefined);
      }
      return leaveService.rejectLeave(leaveId, comments.trim());
    },
    onSuccess: () => {
      invalidate();
      showSuccess(action === 'approve' ? 'Leave request approved' : 'Leave request rejected');
      setComments('');
      onClose();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err.response?.data?.message || err.message || 'Action failed');
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    setComments('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {action === 'approve' ? 'Approve Leave Request' : 'Reject Leave Request'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {action === 'approve'
            ? 'Are you sure you want to approve this leave request?'
            : 'Are you sure you want to reject this leave request? Please provide a reason.'}
        </Typography>
        {mutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(mutation.error as Error)?.message || 'Action failed. Please try again.'}
          </Alert>
        )}
        <TextField
          fullWidth
          multiline
          rows={3}
          label={action === 'approve' ? 'Comments (optional)' : 'Rejection reason'}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder={
            action === 'approve'
              ? 'Add any comments for the employee...'
              : 'Explain why this request is being rejected...'
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={() => mutation.mutate()}
          variant="contained"
          color={action === 'approve' ? 'success' : 'error'}
          disabled={mutation.isPending || !leaveId || (action === 'reject' && !comments.trim())}
        >
          {mutation.isPending ? 'Processing...' : action === 'approve' ? 'Approve' : 'Reject'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveApprovalModal;
